import type { LucideProps } from 'lucide-react'
import {
  AlignJustify,
  ArrowLeft,
  ArrowRight,
  Ban,
  BarChart3,
  Bell,
  Bolt,
  Book,
  Briefcase,
  Building,
  Calendar,
  ChartLine,
  Check,
  CheckCircle,
  ChevronDown,
  ChevronLeft,
  ChevronUp,
  CircleAlert,
  Clock,
  Compass,
  Copy,
  Download,
  Crosshair,
  Eye,
  EyeOff,
  FileOutput,
  FileText,
  Flag,
  Globe,
  Grid2x2,
  Home,
  IdCard,
  Image,
  Info,
  Key,
  Languages,
  Layers,
  LayoutTemplate,
  Link,
  Loader2,
  Lock,
  LogOut,
  Mail,
  MapPin,
  Megaphone,
  Monitor,
  Newspaper,
  Moon,
  Move,
  Network,
  Package,
  Percent,
  Plus,
  Search,
  Send,
  Settings,
  Shield,
  Signpost,
  SlidersHorizontal,
  Sun,
  Table,
  Tags,
  Trash2,
  TriangleAlert,
  User,
  Users,
  Wallet,
  Zap,
  Wrench,
  X,
} from 'lucide-react'

/**
 * Maps old PrimeIcon class names (without the "pi-" prefix or with it)
 * to Lucide React components. Used during the migration from PrimeReact.
 */
const ICON_MAP: Record<string, React.ComponentType<LucideProps>> = {
  'arrow-left': ArrowLeft,
  'arrow-right': ArrowRight,
  'arrows-alt': Move,
  ban: Ban,
  bars: AlignJustify,
  bell: Bell,
  bolt: Bolt,
  book: Book,
  box: Package,
  briefcase: Briefcase,
  building: Building,
  bullseye: Crosshair,
  calendar: Calendar,
  'chart-bar': BarChart3,
  'chart-line': ChartLine,
  check: Check,
  clone: LayoutTemplate,
  'check-circle': CheckCircle,
  'chevron-down': ChevronDown,
  'chevron-left': ChevronLeft,
  'chevron-up': ChevronUp,
  clock: Clock,
  cog: Settings,
  compass: Compass,
  copy: Copy,
  desktop: Monitor,
  download: Download,
  monitor: Monitor,
  directions: Signpost,
  envelope: Mail,
  'exclamation-circle': CircleAlert,
  'exclamation-triangle': TriangleAlert,
  eye: Eye,
  'eye-slash': EyeOff,
  'file-export': FileOutput,
  file: FileText,
  flag: Flag,
  'flag-fill': Flag,
  globe: Globe,
  home: Home,
  'id-card': IdCard,
  image: Image,
  'info-circle': Info,
  key: Key,
  language: Languages,
  layers: Layers,
  link: Link,
  lock: Lock,
  'map-marker': MapPin,
  megaphone: Megaphone,
  moon: Moon,
  newspaper: Newspaper,
  percentage: Percent,
  plus: Plus,
  search: Search,
  send: Send,
  shield: Shield,
  'sign-out': LogOut,
  sitemap: Network,
  'sliders-h': SlidersHorizontal,
  spinner: Loader2,
  sun: Sun,
  table: Table,
  tags: Tags,
  'th-large': Grid2x2,
  times: X,
  trash: Trash2,
  user: User,
  users: Users,
  wallet: Wallet,
  wrench: Wrench,
  zap: Zap,
}

export interface IconProps extends LucideProps {
  /** PrimeIcon name like "pi-check", "check", "pi pi-check" */
  name: string
}

export function Icon({ name, ...props }: IconProps) {
  // Normalize: strip "pi pi-", "pi-", or "pi " prefixes
  const key = name.replace(/^pi[\s-]*/g, '').replace(/^pi-/, '')
  const LucideIcon = ICON_MAP[key]
  if (!LucideIcon) {
    console.warn(`[Icon] No Lucide mapping for "${name}" (key: "${key}")`)
    return null
  }
  return <LucideIcon {...props} />
}
